'use client';

import { cva, VariantProps } from 'class-variance-authority';
import Image from 'next/image';
import { ComponentPropsWithRef } from 'react';
import { cn } from '@/shared/lib/cn';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import './categories.scss';
import { CategoryT, SharedCategoriesProps } from './types';

const categoryVariants = cva('categories__category', {
    variants: {
        variant: {
            default: null,
            carousel: 'categories__category--carousel',
        },
        isSelected: {
            true: 'categories__category--selected',
            false: null,
        },
        isSubcategory: {
            true: 'categories__category--subcategory',
            false: null,
        },
    },
    defaultVariants: {
        variant: 'default',
        isSelected: false,
        isSubcategory: false,
    },
});

type Props = Omit<ComponentPropsWithRef<'button'>, 'name'> &
    CategoryT &
    SharedCategoriesProps &
    VariantProps<typeof categoryVariants> & {
        isImagePriority?: boolean;
    };
export const CategoriesCategory = ({
    name,
    id,
    iconUrl,
    isSelected,
    isIconVisible = true,
    isNameVisible = true,
    variant,
    isSubcategory,
    className,
    ...props
}: Props) => {
    const getLocalizedValue = useGetLocalizedValue();
    const localizedName = getLocalizedValue(name);

    return (
        <button
            className={cn(categoryVariants({ variant, isSelected, isSubcategory }), className)}
            type="button"
            data-testid="categories-category"
            data-id={id}
            data-type={isSubcategory ? 'subcategory' : 'category'}
            data-selected={isSelected}
            {...props}
        >
            {iconUrl && isIconVisible && (
                <Image
                    width={44}
                    height={44}
                    src={iconUrl}
                    alt={`${localizedName}-icon`}
                    className="categories__icon"
                    data-testid="categories-category-icon"
                />
            )}
            {isNameVisible && <span data-testid="categories-category-name">{localizedName}</span>}
        </button>
    );
};
